/**
 * Real Name Edit — /(app)/settings/account/real-name
 *
 * Optional free-form identity shown on the PUBLIC profile so friends can
 * tell who is who (real name, first name, abbreviated email — anything).
 * No uniqueness, no charset rules; empty clears it.
 * Save → PATCH /api/v1/users/me { realName }.
 */

import { COMPACT_HEADER_STYLE } from '../../../../lib/gameChrome'
import React, { useState } from 'react'
import { View, Text, TouchableOpacity, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../../hooks/useOrientation'
import { useAuth } from '../../../../hooks/useAuth'
import { apiPatch } from '../../../../lib/api'
import { Button } from '../../../../components/ui/Button'
import { Input } from '../../../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../../../components/ui/Toast'
import type { UserResponse } from '@warcarp/api-types'
import { goBackOr } from '../../../../lib/goBack'
import { REAL_NAME_MAX } from '@warcarp/api-types'

// Mirror the API's zod limit (users.ts updateSchema).

export default function RealNameEditScreen() {
  const { user, refreshUser } = useAuth()
  const { showToast } = useToast()
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined

  const [realName, setRealName] = useState(user?.realName ?? '')
  const [saving, setSaving] = useState(false)

  const changed = realName.trim() !== (user?.realName ?? '')

  async function handleSave() {
    setSaving(true)
    try {
      // Empty string clears the field server-side.
      await apiPatch<UserResponse>('/api/v1/users/me', { realName: realName.trim() })
      await refreshUser()
      showToast('Real name saved', 'success')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/account')}
          accessibilityLabel="Back to account settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Real Name</Text>
      </View>

      <KeyboardAwareScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 20,
          alignItems: isLandscape ? 'center' : undefined,
        }}
        keyboardDismissMode="on-drag"
      >
        <View style={isLandscape ? { width: contentWidth } : { width: '100%' }}>
          <View className="bg-paper rounded-2xl p-4 gap-3">
            <Text className="text-ink font-stencil uppercase">Real Name</Text>
            <Input
              value={realName}
              onChangeText={setRealName}
              placeholder="e.g. Adam, or adam@…"
              autoCapitalize="words"
              autoCorrect={false}
              maxLength={REAL_NAME_MAX}
            />
            <Text className="font-typed text-ink-soft text-xs italic">
              // Optional. Shown on your public profile so friends can tell
              who is who. Leave empty to hide it.
            </Text>
            <Button
              title="Save Changes"
              onPress={handleSave}
              loading={saving}
              disabled={!changed || saving}
            />
          </View>
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
